/**
 * Cards de empresa da Composição de Saldo — a porta de entrada da tela.
 *
 * Antes, a tela exigia buscar a empresa para mostrar qualquer coisa: quem
 * abria o módulo via uma tela vazia. Agora ela abre com a carteira inteira em
 * cards; clicar num card faz exatamente o que selecionar a empresa fazia. A
 * busca continua existindo, como FILTRO — não como porta de entrada.
 *
 * HONESTIDADE DO CARD: o selo de status descreve o que o dado diz ("fecha",
 * "sem movimento no período", "faltam N aberturas"), nunca conferência
 * ("tudo certo", "conferido", "sem problemas"). O balancete é um snapshot que
 * pode não conter todas as contas com movimento — um card verde não prova que
 * a empresa foi conferida. Ver o bloco de regras em `visaoGeral.ts`.
 */

import React from 'react';
import { Badge, Box, Button, Flex, Grid, Text } from '@chakra-ui/react';
import { Buildings, CaretLeft, CaretRight } from 'phosphor-react';

import { ROTULO_NATUREZA, fmtMoney } from './balancete';
import {
  COR_STATUS,
  EXPLICA_STATUS,
  IEmpresaVisaoGeral,
  ROTULO_STATUS,
  faixaExibida,
  detalheStatus,
  explicaGrupo,
  fmtCnpj,
  fmtLocal,
  rotuloDoGrupo,
} from './visaoGeral';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  brand: '#1B88F5',
  linha: 'rgba(15, 23, 42, 0.10)',
} as const;

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

/** Um card de empresa: clicar equivale a selecionar a empresa na busca. */
function EmpresaCard({
  empresa,
  onAbrir,
}: {
  empresa: IEmpresaVisaoGeral;
  onAbrir: (e: IEmpresaVisaoGeral) => void;
}) {
  const cor = COR_STATUS[empresa.status];
  const detalhe = detalheStatus(empresa);

  return (
    <Box
      as="button"
      onClick={() => onAbrir(empresa)}
      textAlign="left"
      w="full"
      h="full"
      display="flex"
      flexDirection="column"
      gap={2}
      p={4}
      borderRadius="xl"
      border="1px solid"
      borderColor={COR.linha}
      bg="rgba(255, 255, 255, 0.75)"
      transition="all 0.15s ease"
      _hover={{
        borderColor: COR.brand,
        boxShadow: '0 8px 20px -10px rgba(27, 136, 245, 0.55)',
        transform: 'translateY(-2px)',
      }}
      aria-label={`Abrir a composição de saldo de ${empresa.nome}`}
      {...{ type: 'button' }}
    >
      {/* Identificação: external_id é o número que o usuário conhece da empresa. */}
      <Flex align="center" gap={2} w="full" minW={0}>
        <Box color={COR.faint} flexShrink={0}>
          <Buildings size={16} weight="duotone" />
        </Box>
        {empresa.external_id != null && (
          <Text fontSize="11px" fontWeight="700" color={COR.muted} sx={NUM} flexShrink={0}>
            {empresa.external_id}
          </Text>
        )}
        <Text fontSize="13.5px" fontWeight="700" color={COR.ink} noOfLines={2} title={empresa.nome}>
          {empresa.nome}
        </Text>
      </Flex>

      <Text fontSize="11px" color={COR.faint} sx={NUM} noOfLines={1}>
        {fmtCnpj(empresa.cnpj)} · {fmtLocal(empresa)}
      </Text>

      {/* Marca de grupo matriz/filiais (raiz do CNPJ). Só existe quando há o
          que dizer — o backend omite na matriz sozinha, que é a maioria. */}
      {empresa.grupo && (
        <Badge
          alignSelf="flex-start"
          maxW="full"
          isTruncated
          borderRadius="full"
          px={2}
          py={0.5}
          fontSize="10px"
          fontWeight="700"
          textTransform="none"
          bg="rgba(27, 136, 245, 0.08)"
          color={COR.brand}
          border="1px solid"
          borderColor="rgba(27, 136, 245, 0.35)"
          title={explicaGrupo(empresa.grupo)}
          data-testid="marca-grupo"
        >
          {rotuloDoGrupo(empresa.grupo)}
        </Badge>
      )}

      <Box flex="1" />

      {/* Selo do status. O `title` explica o que o status SIGNIFICA — e, no
          verde, diz o que ele não garante. */}
      <Badge
        alignSelf="flex-start"
        borderRadius="full"
        px={2.5}
        py={1}
        fontSize="10.5px"
        fontWeight="700"
        textTransform="none"
        whiteSpace="normal"
        bg={cor.bg}
        color={cor.fg}
        border="1px solid"
        borderColor={cor.borda}
        title={EXPLICA_STATUS[empresa.status]}
      >
        {ROTULO_STATUS[empresa.status]}
      </Badge>

      {detalhe && (
        <Text fontSize="11px" color={COR.muted} noOfLines={1} title={detalhe}>
          {detalhe}
        </Text>
      )}

      {/* Números do recorte. Em empresa sem movimento não há o que mostrar:
          "0 contas · 0 lançamentos" só repetiria o selo cinza. */}
      {empresa.status !== 'sem_dado' && (
        <Flex align="center" gap={1.5} wrap="wrap" fontSize="11px" color={COR.muted}>
          <Text sx={NUM}>{empresa.qtd_contas} contas</Text>
          <Text color={COR.faint}>·</Text>
          <Text sx={NUM}>{empresa.qtd_lancamentos} lançamentos</Text>
          {empresa.status === 'divergente' && (
            <>
              <Text color={COR.faint}>·</Text>
              <Text sx={NUM} color={COR_STATUS.divergente.fg} fontWeight="700">
                {ROTULO_NATUREZA.D}−{ROTULO_NATUREZA.C} {fmtMoney(empresa.diferenca)}
              </Text>
            </>
          )}
        </Flex>
      )}
    </Box>
  );
}

interface Props {
  /** Empresas JÁ recortadas na página corrente (ver `EMPRESAS_POR_PAGINA`). */
  empresas: IEmpresaVisaoGeral[];
  onAbrir: (e: IEmpresaVisaoGeral) => void;
  pagina: number;
  paginas: number;
  /** Total do recorte atual (filtro aplicado), para o "N de M". */
  totalFiltrado: number;
  onPagina: (p: number) => void;
  carregando?: boolean;
}

/** Grade paginada de cards — recorte feito no cliente (`EMPRESAS_POR_PAGINA`). */
export function EmpresasCards({
  empresas,
  onAbrir,
  pagina,
  paginas,
  totalFiltrado,
  onPagina,
  carregando,
}: Props) {
  const { primeiro, ultimo } = faixaExibida(pagina, empresas.length, totalFiltrado);

  return (
    <>
      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
          xl: 'repeat(5, 1fr)',
        }}
        gap={3}
      >
        {empresas.map(e => (
          <EmpresaCard key={e.empresa_id} empresa={e} onAbrir={onAbrir} />
        ))}
      </Grid>

      {paginas > 1 && (
        <Flex
          mt={4}
          align="center"
          gap={2}
          wrap="wrap"
          justify="space-between"
          borderTop="1px solid"
          borderColor={COR.linha}
          pt={3}
        >
          <Text fontSize="12.5px" color={COR.muted}>
            Mostrando{' '}
            <b>
              {primeiro}–{ultimo}
            </b>{' '}
            de <b>{totalFiltrado}</b> empresas
          </Text>
          <Flex align="center" gap={2}>
            <Button
              size="xs"
              variant="outline"
              borderRadius="md"
              leftIcon={<CaretLeft size={12} weight="bold" />}
              isDisabled={pagina <= 1 || carregando}
              onClick={() => onPagina(pagina - 1)}
            >
              Anterior
            </Button>
            <Text fontSize="12.5px" color={COR.ink} fontWeight="600" sx={NUM}>
              Página {pagina} de {paginas}
            </Text>
            <Button
              size="xs"
              variant="outline"
              borderRadius="md"
              rightIcon={<CaretRight size={12} weight="bold" />}
              isDisabled={pagina >= paginas || carregando}
              onClick={() => onPagina(pagina + 1)}
            >
              Próxima
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
}
